import { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Box,
} from "@mui/material";
import AsyncSelect from "react-select/async";
import { Search as SearchIcon } from "@mui/icons-material";
import { LanguageContext } from "App";
import SearchAccSessionReq from "../../../Classes/Models/Requests/AccountingSessions/CreateAccountingSessionReq";
import SearchUserModel from "../../../Classes/Models/Requests/Users/SearchUser";
import UsersApi from "../../../Classes/API/UsersApi";
import NewQuickDate from "../../../components/QuickDate/NewQuickDate";
import QuickDateMultiRange from "../../../components/QuickDate/QuickDateMultiRange";
import ThreeStateCheckbox from "../../../components/ThreeStateCheckbox";

const SearchSingleSessions = ({ setSearchReq, GetTableData }) => {
  const { translations } = useContext(LanguageContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    ...new SearchAccSessionReq(),
  });

  const [dateReset, setDateReset] = useState(false);

  const formatParams = (data) => {
    return {
      ...data,
      appointmentDateFrom: data.appointmentDateFrom
        ? data.appointmentDateFrom + "T00:00:00"
        : undefined,
      appointmentDateTo: data.appointmentDateTo
        ? data.appointmentDateTo + "T23:59:59"
        : undefined,
      timeStampFrom: data.timeStampFrom
        ? data.timeStampFrom + "T00:00:00"
        : undefined,
      timeStampTo: data.timeStampTo
        ? data.timeStampTo + "T23:59:59"
        : undefined,
    };
  };

  useEffect(() => {
    const params = Object.fromEntries([...searchParams]);
    if (Object.keys(params).length > 0) {
      const initialData = { ...new SearchAccSessionReq(), ...params };

      if (params.isPaidFor)
        initialData.isPaidFor =
          params.isPaidFor === "true"
            ? true
            : params.isPaidFor === "false"
              ? false
              : undefined;
      if (params.isException)
        initialData.isException =
          params.isException === "true"
            ? true
            : params.isException === "false"
              ? false
              : undefined;

      const formatted = formatParams(initialData);
      setFormData(initialData);
      setSearchReq(formatted);

      if (GetTableData) {
        GetTableData(formatted);
      }
    }
  }, []);

  const getPatients = async (inputValue = "") => {
    const searchReq = new SearchUserModel();
    searchReq.userType = "Patient";
    searchReq.name = inputValue;
    const res = await UsersApi.SearchUsers(searchReq);
    return res.status === "Success"
      ? res.data.list.map((item) => ({
          value: { ...item.getPatient, name: item.name },
          label: item.name,
        }))
      : [];
  };

  const getTherapists = async (inputValue = "") => {
    const searchReq = new SearchUserModel();
    searchReq.userType = "Therapist";
    searchReq.name = inputValue;
    const res = await UsersApi.SearchUsers(searchReq);
    return res.status === "Success"
      ? res.data.list.map((item) => ({
          value: { ...item.getTherapist, name: item.name },
          label: item.name,
        }))
      : [];
  };

  const handleInputChange = (field, value, label) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "patientId" && { patientName: label }),
      ...(field === "therapist" && { therapistId: value?.id }),
    }));
  };

  const onSearch = () => {
    const newParams = new URLSearchParams();

    Object.keys(formData).forEach((key) => {
      const value = formData[key];
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        typeof value !== "object"
      ) {
        newParams.set(key, value.toString());
      } else if (key === "patient" && value?.id) {
        newParams.set("patientId", value.id);
      } else if (key === "therapist" && value?.id) {
        newParams.set("therapistId", value.id);
      }
    });

    setSearchParams(newParams);

    const formatted = formatParams({
      ...formData,
      patientId: formData.patient?.id || formData.patientId,
      therapistId: formData.therapist?.id || formData.therapistId,
    });
    setSearchReq(formatted);

    if (GetTableData) {
      GetTableData(formatted);
    }
  };

  const handleClear = () => {
    const resetData = new SearchAccSessionReq();
    setFormData(resetData);
    setSearchParams({});
    const formatted = formatParams(resetData);
    setSearchReq(formatted);
    setDateReset(true);
    setTimeout(() => setDateReset(false), 100);
    if (GetTableData) GetTableData(formatted);
  };

  const customSelectStyles = {
    placeholder: (provided) => ({ ...provided, fontSize: "18px" }),
    singleValue: (provided) => ({ ...provided, fontSize: "18px" }),
    input: (provided) => ({ ...provided, fontSize: "21px" }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="medium" gutterBottom>
          {translations.createAccountsessionSingleSessionSearchSearch}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={
                translations.createAccountsessionSingleSessionSearchAccountingSessionId
              }
              value={formData.sessionId || ""}
              onChange={(e) =>
                handleInputChange("sessionId", e.target.value || undefined)
              }
              sx={{
                "& .MuiInputLabel-root": { fontSize: "18px" },
                "& .MuiInputBase-input": { fontSize: "18px" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <AsyncSelect
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={getPatients}
              value={
                formData.patientId && formData.patientName
                  ? { value: formData.patientId, label: formData.patientName }
                  : null
              }
              onChange={(selected) =>
                handleInputChange(
                  "patientId",
                  selected?.value.id || null,
                  selected?.label || "",
                )
              }
              placeholder={
                translations.createAccountsessionSingleSessionSearchSelectPatient
              }
              styles={customSelectStyles}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label={
                translations.createAccountsessionSingleSessionSearchSessionName
              }
              value={formData.sessionName || ""}
              onChange={(e) =>
                handleInputChange("sessionName", e.target.value || undefined)
              }
              sx={{
                "& .MuiInputLabel-root": { fontSize: "18px" },
                "& .MuiInputBase-input": { fontSize: "18px" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label={
                translations.createAccountsessionSingleSessionSearchPatientMobileNumber
              }
              value={formData.phoneNumber || ""}
              onChange={(e) =>
                handleInputChange("phoneNumber", e.target.value || undefined)
              }
              sx={{
                "& .MuiInputLabel-root": { fontSize: "18px" },
                "& .MuiInputBase-input": { fontSize: "18px" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <AsyncSelect
              isClearable
              cacheOptions
              defaultOptions
              loadOptions={getTherapists}
              value={
                formData.therapist
                  ? {
                      value: formData.therapist,
                      label: formData.therapist.name,
                    }
                  : null
              }
              onChange={(selected) =>
                handleInputChange("therapist", selected?.value)
              }
              placeholder={
                translations.createAccountsessionSingleSessionSearchSelectbytherapistname
              }
              styles={customSelectStyles}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={5}>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                label="Final Price From"
                type="number"
                value={formData.finalPriceMin || ""}
                onChange={(e) =>
                  handleInputChange(
                    "finalPriceMin",
                    e.target.value || undefined,
                  )
                }
                size="small"
                style={{ width: "250px" }}
                sx={{
                  "& .MuiInputLabel-root": { fontSize: "18px" },
                  "& .MuiInputBase-input": { fontSize: "18px" },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: "18px" }}>
                to
              </Typography>
              <TextField
                label="Final Price To"
                type="number"
                value={formData.finalPriceMax || ""}
                onChange={(e) =>
                  handleInputChange(
                    "finalPriceMax",
                    e.target.value || undefined,
                  )
                }
                size="small"
                style={{ width: "250px" }}
                sx={{
                  "& .MuiInputLabel-root": { fontSize: "18px" },
                  "& .MuiInputBase-input": { fontSize: "18px" },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" flexWrap="wrap" gap={3}>
              <div style={{ flex: 1 }}>
                <ThreeStateCheckbox
                  label={
                    translations.createAccountsessionSingleSessionSearchIsPaidFor
                  }
                  value={formData.isPaidFor}
                  onChange={(value) => handleInputChange("isPaidFor", value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <ThreeStateCheckbox
                  label={
                    translations.createAccountsessionSingleSessionSearchExceptions
                  }
                  value={formData.isException}
                  onChange={(value) => handleInputChange("isException", value)}
                />
              </div>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontSize: "18px" }}
            >
              {
                translations.createAccountsessionSingleSessionSearchTimeStampDateRange
              }
            </Typography>
            <NewQuickDate
              reset={dateReset}
              setFrom={(date) => handleInputChange("timeStampFrom", date)}
              setTo={(date) => handleInputChange("timeStampTo", date)}
              from={formData.timeStampFrom}
              to={formData.timeStampTo}
              placeholder="Quick Last Updated"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.showRefunds}
                  onChange={(event) =>
                    handleInputChange(
                      "showRefunds",
                      event.target.checked || undefined,
                    )
                  }
                  style={{ marginTop: "7px" }}
                />
              }
              label={<span>Show Refunds</span>}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontSize: "18px" }}
            >
              {
                translations.createAccountsessionSingleSessionSearchAppointmentDateRange
              }
            </Typography>
            <QuickDateMultiRange
              reset={dateReset}
              setRanges={(ranges) =>
                handleInputChange("appointmentDateRanges", ranges)
              }
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          <Button
            style={{ color: "white", backgroundColor: "#3560AD" }}
            variant="contained"
            onClick={handleClear}
          >
            {
              translations.createAccountsessionSingleSessionSearchSearchpatientsClear
            }
          </Button>
          <Button
            style={{ color: "white", backgroundColor: "#3560AD" }}
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={onSearch}
          >
            {translations.createAccountsessionSingleSessionSearchSearchpatients}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SearchSingleSessions;
